/**
 * SOW-Level Integration Settings
 * Configure Slack/Teams for an entire engagement (SOW + Change Orders)
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { NotificationHelpDialog } from './NotificationHelpDialog';
import { 
  Settings, 
  Slack, 
  ExternalLink, 
  Check, 
  X, 
  Loader2,
  AlertCircle,
  Info,
  TestTube,
  CheckCircle2,
  Briefcase
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

// MS Teams icon (lucide doesn't have it)
const TeamsIcon = () => (
  <svg className="size-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.19 8.77a3.58 3.58 0 0 0-3.29-3.22h-4.36a.58.58 0 0 0-.58.58v7.78a.58.58 0 0 0 .58.58h4.36a3.58 3.58 0 0 0 3.29-3.22v-2.5zm-8.83-2.64H5.64a3 3 0 0 0-3 3v5.44a3 3 0 0 0 3 3h4.72a.58.58 0 0 0 .58-.58V6.71a.58.58 0 0 0-.58-.58z"/>
  </svg>
);

interface SOWIntegrationSettingsProps {
  sowId: string;
  sowName: string;
  accessToken: string;
  currentUser: any;
}

interface IntegrationConfig {
  webhookUrl: string;
  platform: 'slack' | 'teams';
  channelName?: string;
  enabled: boolean;
  notificationTypes?: {
    dueTomorrow?: boolean;
    overdue?: boolean;
    assignments?: boolean;
    statusChanges?: boolean;
    comments?: boolean;
  };
}

export function SOWIntegrationSettings({
  sowId,
  sowName,
  accessToken,
  currentUser,
}: SOWIntegrationSettingsProps) {
  const [slackConfig, setSlackConfig] = useState<IntegrationConfig | null>(null);
  const [teamsConfig, setTeamsConfig] = useState<IntegrationConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Form states
  const [slackWebhook, setSlackWebhook] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [slackEnabled, setSlackEnabled] = useState(true);
  const [slackNotifs, setSlackNotifs] = useState({
    dueTomorrow: true,
    overdue: true,
    assignments: true,
    statusChanges: true,
    comments: false,
  });

  const [teamsWebhook, setTeamsWebhook] = useState('');
  const [teamsChannel, setTeamsChannel] = useState('');
  const [teamsEnabled, setTeamsEnabled] = useState(true);
  const [teamsNotifs, setTeamsNotifs] = useState({
    dueTomorrow: true,
    overdue: true,
    assignments: true,
    statusChanges: true,
    comments: false,
  });

  useEffect(() => {
    loadIntegrations();
  }, [sowId]);

  const loadIntegrations = async () => {
    try {
      setLoading(true);

      // Load Slack config
      const slackRes = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/integrations/sow/${sowId}/slack`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (slackRes.ok) {
        const slackData = await slackRes.json();
        if (slackData.config) {
          setSlackConfig(slackData.config);
          setSlackWebhook(slackData.config.webhookUrl);
          setSlackChannel(slackData.config.channelName || '');
          setSlackEnabled(slackData.config.enabled);
          setSlackNotifs(slackData.config.notificationTypes || slackNotifs);
        }
      }

      // Load Teams config
      const teamsRes = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/integrations/sow/${sowId}/teams`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (teamsRes.ok) {
        const teamsData = await teamsRes.json();
        if (teamsData.config) {
          setTeamsConfig(teamsData.config);
          setTeamsWebhook(teamsData.config.webhookUrl);
          setTeamsChannel(teamsData.config.channelName || '');
          setTeamsEnabled(teamsData.config.enabled);
          setTeamsNotifs(teamsData.config.notificationTypes || teamsNotifs);
        }
      }
    } catch (error: any) {
      console.error('[SOWIntegrations] Error loading integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const saveIntegration = async (platform: 'slack' | 'teams') => {
    try {
      setSaving(true);

      const isSlack = platform === 'slack';
      const webhookUrl = isSlack ? slackWebhook : teamsWebhook;
      const channelName = isSlack ? slackChannel : teamsChannel;
      const enabled = isSlack ? slackEnabled : teamsEnabled;
      const notificationTypes = isSlack ? slackNotifs : teamsNotifs;

      if (!webhookUrl) {
        toast.error('Webhook URL is required');
        return;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/integrations/sow/${sowId}/${platform}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            webhookUrl,
            channelName,
            enabled,
            notificationTypes,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save integration');
      }

      const result = await response.json();
      
      if (isSlack) {
        setSlackConfig(result.config);
      } else {
        setTeamsConfig(result.config);
      }

      toast.success(`${isSlack ? 'Slack' : 'Teams'} integration saved`);
    } catch (error: any) {
      console.error(`[SOWIntegrations] Error saving ${platform}:`, error);
      toast.error(error.message || `Failed to save ${platform} integration`);
    } finally {
      setSaving(false);
    }
  };

  const testIntegration = async (platform: 'slack' | 'teams') => {
    try {
      setTesting(true);

      const isSlack = platform === 'slack';
      const webhookUrl = isSlack ? slackWebhook : teamsWebhook;

      if (!webhookUrl) {
        toast.error('Please enter a webhook URL first');
        return;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/integrations/sow/${sowId}/${platform}/test`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            webhookUrl,
            userName: currentUser.name || 'Test User',
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Test failed');
      }

      toast.success('Test message sent. Check your channel.');
    } catch (error: any) {
      console.error(`[SOWIntegrations] Error testing ${platform}:`, error);
      toast.error(error.message || 'Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  const removeIntegration = async (platform: 'slack' | 'teams') => {
    if (!confirm(`Remove ${platform === 'slack' ? 'Slack' : 'Teams'} integration?`)) {
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/integrations/sow/${sowId}/${platform}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to remove integration');
      }

      if (platform === 'slack') {
        setSlackConfig(null);
        setSlackWebhook('');
        setSlackChannel('');
      } else {
        setTeamsConfig(null);
        setTeamsWebhook('');
        setTeamsChannel('');
      }

      toast.success(`${platform === 'slack' ? 'Slack' : 'Teams'} integration removed`);
    } catch (error: any) {
      console.error(`[SOWIntegrations] Error removing ${platform}:`, error);
      toast.error(`Failed to remove ${platform} integration`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="size-5" />
                Engagement Integrations
              </CardTitle>
              <CardDescription>
                Connect this engagement ({sowName}) to a dedicated Slack or Teams channel. All team members will receive notifications for config items in this engagement.
              </CardDescription>
            </div>
            <NotificationHelpDialog context="engagement" />
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="slack" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="slack" className="flex items-center gap-2">
                <Slack className="size-4" />
                Slack
                {slackConfig && <CheckCircle2 className="size-3 text-green-600" />}
              </TabsTrigger>
              <TabsTrigger value="teams" className="flex items-center gap-2">
                <TeamsIcon />
                Teams
                {teamsConfig && <CheckCircle2 className="size-3 text-green-600" />}
              </TabsTrigger>
            </TabsList>

            {/* Slack Tab */}
            <TabsContent value="slack" className="space-y-4 mt-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-2">
                <p className="font-medium text-blue-900">How to get your Slack webhook:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-800">
                  <li>Go to your Slack workspace</li>
                  <li>Navigate to the channel where you want notifications (e.g., #{sowName.toLowerCase().replace(/\s+/g, '-')})</li>
                  <li>Click the channel name → Integrations → Add an app → Search "Incoming Webhooks"</li>
                  <li>Click "Add" and copy the webhook URL</li>
                </ol>
                <a
                  href="https://api.slack.com/messaging/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:underline"
                >
                  View Slack documentation <ExternalLink className="size-3" />
                </a>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="slack-webhook">Webhook URL *</Label>
                  <Input
                    id="slack-webhook"
                    type="url"
                    placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXX"
                    value={slackWebhook}
                    onChange={(e) => setSlackWebhook(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="slack-channel">Channel Name (optional)</Label>
                  <Input
                    id="slack-channel"
                    placeholder="#scopify-acme"
                    value={slackChannel}
                    onChange={(e) => setSlackChannel(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    For display purposes only
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="slack-enabled"
                    checked={slackEnabled}
                    onCheckedChange={setSlackEnabled}
                  />
                  <Label htmlFor="slack-enabled">Enable Slack notifications</Label>
                </div>

                <div className="space-y-3 pt-2">
                  <p className="text-sm font-medium">Notification Types:</p>
                  <div className="space-y-2 pl-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="slack-tomorrow"
                        checked={slackNotifs.dueTomorrow}
                        onCheckedChange={(checked) => setSlackNotifs({ ...slackNotifs, dueTomorrow: checked })}
                      />
                      <Label htmlFor="slack-tomorrow" className="text-sm font-normal">Items due tomorrow</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="slack-overdue"
                        checked={slackNotifs.overdue}
                        onCheckedChange={(checked) => setSlackNotifs({ ...slackNotifs, overdue: checked })}
                      />
                      <Label htmlFor="slack-overdue" className="text-sm font-normal">Overdue items</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="slack-assignments"
                        checked={slackNotifs.assignments}
                        onCheckedChange={(checked) => setSlackNotifs({ ...slackNotifs, assignments: checked })}
                      />
                      <Label htmlFor="slack-assignments" className="text-sm font-normal">New assignments</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="slack-status"
                        checked={slackNotifs.statusChanges}
                        onCheckedChange={(checked) => setSlackNotifs({ ...slackNotifs, statusChanges: checked })}
                      />
                      <Label htmlFor="slack-status" className="text-sm font-normal">Status changes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="slack-comments"
                        checked={slackNotifs.comments}
                        onCheckedChange={(checked) => setSlackNotifs({ ...slackNotifs, comments: checked })}
                      />
                      <Label htmlFor="slack-comments" className="text-sm font-normal">New comments</Label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={() => saveIntegration('slack')} disabled={saving || !slackWebhook}>
                    {saving ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                    Save Slack Integration
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => testIntegration('slack')}
                    disabled={testing || !slackWebhook}
                  >
                    {testing ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                    Send Test Message
                  </Button>
                  {slackConfig && (
                    <Button
                      variant="destructive"
                      onClick={() => removeIntegration('slack')}
                      disabled={saving}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Teams Tab */}
            <TabsContent value="teams" className="space-y-4 mt-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm space-y-2">
                <p className="font-medium text-purple-900">How to get your Teams webhook:</p>
                <ol className="list-decimal list-inside space-y-1 text-purple-800">
                  <li>Go to your Microsoft Teams</li>
                  <li>Navigate to the channel where you want notifications</li>
                  <li>Click the ⋯ menu → Connectors → Incoming Webhook</li>
                  <li>Name it "PathKit Notifications" and upload an icon (optional)</li>
                  <li>Copy the webhook URL</li>
                </ol>
                <a
                  href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-purple-600 hover:underline"
                >
                  View Teams documentation <ExternalLink className="size-3" />
                </a>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="teams-webhook">Webhook URL *</Label>
                  <Input
                    id="teams-webhook"
                    type="url"
                    placeholder="https://outlook.office.com/webhook/..."
                    value={teamsWebhook}
                    onChange={(e) => setTeamsWebhook(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="teams-channel">Channel Name (optional)</Label>
                  <Input
                    id="teams-channel"
                    placeholder="PathKit Notifications"
                    value={teamsChannel}
                    onChange={(e) => setTeamsChannel(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    For display purposes only
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="teams-enabled"
                    checked={teamsEnabled}
                    onCheckedChange={setTeamsEnabled}
                  />
                  <Label htmlFor="teams-enabled">Enable Teams notifications</Label>
                </div>

                <div className="space-y-3 pt-2">
                  <p className="text-sm font-medium">Notification Types:</p>
                  <div className="space-y-2 pl-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="teams-tomorrow"
                        checked={teamsNotifs.dueTomorrow}
                        onCheckedChange={(checked) => setTeamsNotifs({ ...teamsNotifs, dueTomorrow: checked })}
                      />
                      <Label htmlFor="teams-tomorrow" className="text-sm font-normal">Items due tomorrow</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="teams-overdue"
                        checked={teamsNotifs.overdue}
                        onCheckedChange={(checked) => setTeamsNotifs({ ...teamsNotifs, overdue: checked })}
                      />
                      <Label htmlFor="teams-overdue" className="text-sm font-normal">Overdue items</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="teams-assignments"
                        checked={teamsNotifs.assignments}
                        onCheckedChange={(checked) => setTeamsNotifs({ ...teamsNotifs, assignments: checked })}
                      />
                      <Label htmlFor="teams-assignments" className="text-sm font-normal">New assignments</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="teams-status"
                        checked={teamsNotifs.statusChanges}
                        onCheckedChange={(checked) => setTeamsNotifs({ ...teamsNotifs, statusChanges: checked })}
                      />
                      <Label htmlFor="teams-status" className="text-sm font-normal">Status changes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="teams-comments"
                        checked={teamsNotifs.comments}
                        onCheckedChange={(checked) => setTeamsNotifs({ ...teamsNotifs, comments: checked })}
                      />
                      <Label htmlFor="teams-comments" className="text-sm font-normal">New comments</Label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={() => saveIntegration('teams')} disabled={saving || !teamsWebhook}>
                    {saving ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                    Save Teams Integration
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => testIntegration('teams')}
                    disabled={testing || !teamsWebhook}
                  >
                    {testing ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                    Send Test Message
                  </Button>
                  {teamsConfig && (
                    <Button
                      variant="destructive"
                      onClick={() => removeIntegration('teams')}
                      disabled={saving}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}